import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createTranslator, reportMissingKey } from '../_helpers/i18n.js';
import { createFormatters } from '../_helpers/formatters.js';
import { DEVICE_ONLY_PREFERENCE } from '../_helpers/localePreference.js';
import {
  LOCALE_STORAGE_KEY,
  applyDocumentLocale,
  resolveInitialLocale,
  restoreDocumentLocale,
  writeStoredLocale,
} from '../locales/bootstrap.js';
import {
  DEFAULT_LOCALE,
  canonicalizeLocale,
  directionOf,
  getMessages,
  isActivatable,
  pluralLocaleOf,
  selectableLocales,
} from '../locales/index.js';

/**
 * Locale provider (ENG-387). Starts on the locale `bootstrapLocalization`
 * (ENG-378, `src/locales/bootstrap.js`) resolved before the first render —
 * stored preference, then the browser languages, then en-US — keeps
 * `<html lang dir>` in step with the active locale and hands components
 * `t()` and the locale's `format`ters.
 *
 * This is the device layer: `setLocale` here applies a choice and keeps it in
 * `localStorage` for this browser. `LocalePreferenceSync` (ENG-379, inside
 * the Router) layers the signed-in listener's account preference on top by
 * re-providing this context with a `setLocale` that also saves to the
 * account and a `preference` that says whether it did; without it — tests,
 * isolated mounts, a signed-out visitor — `preference` is
 * DEVICE_ONLY_PREFERENCE and choices stay on the device.
 *
 * Components render without a provider too (tests, isolated mounts): the
 * context default is English, the same way NotificationsContext defaults to
 * DISABLED_NOTIFICATIONS.
 */
export { LOCALE_STORAGE_KEY, resolveInitialLocale };

export const buildLocaleValue = (locale, setLocale = () => false, preference = DEVICE_ONLY_PREFERENCE) => ({
  locale,
  dir: directionOf(locale),
  locales: selectableLocales(),
  setLocale,
  preference,
  t: createTranslator({
    locale,
    messages: getMessages(locale),
    fallbackMessages: getMessages(DEFAULT_LOCALE),
    pluralLocale: pluralLocaleOf(locale),
    fallbackLocale: DEFAULT_LOCALE,
    onMissing: reportMissingKey,
  }),
  format: createFormatters(locale),
});

const LocaleContext = createContext(buildLocaleValue(DEFAULT_LOCALE));

export const LocaleProvider = ({ children, initialLocale }) => {
  const [locale, setLocaleState] = useState(() => resolveInitialLocale(initialLocale));

  // Mirror the locale onto the document (the bootstrap did so before the
  // first paint; this keeps it current across switches) and put back
  // whatever was there when the provider goes away.
  useEffect(() => {
    const previous = applyDocumentLocale(locale);
    return () => restoreDocumentLocale(previous);
  }, [locale]);

  /** Switch locale; refuses (and returns false for) anything not activatable. */
  const setLocale = useCallback((tag) => {
    const next = canonicalizeLocale(tag);
    if (!next || !isActivatable(next)) return false;
    writeStoredLocale(next);
    setLocaleState(next);
    return true;
  }, []);

  const value = useMemo(() => buildLocaleValue(locale, setLocale), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);

/** Shorthand for components that only translate. */
export const useT = () => useLocale().t;

/** Shorthand for components that only format dates, numbers or money; see `src/_helpers/formatters.js`. */
export const useFormatters = () => useLocale().format;

export default LocaleContext;
